import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../pages/chat/api";
import {
  UploadIconSvg,
  ChatIconSvg,
  EyeButton,
  CreateManageIconSvg,
  SwitchToEditorViewIconSvg,
  SwitchToSectionViewIconSvg,
  DownloadIconSvg,
  GenerateProposalIconSvg,
  TeamIcon,
  UploadFile
} from "./HeaderSvgs";
import ReviewerActionGate from "../ReviewerActionGate";
import NotificationBell from "../Notifications/NotificationBell";
import "./Header.css";

const formatDueDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const Header = ({
  name,
  icon,
  bell,
  image,
  search,
  askCoPiolt,
  contractKey,
  invoiceKey,
  contractId,
  invoicesId,
  invoiceUrl,
  excelKey,
  excelUrl,
  onGoToInvoices,
  onClick,
  isMenuOpen,
  isProposalPage = false,
  proposalProgress = { completed: 0, total: 0 },
  isGenerating = false,
  onGenerateProposal,
  folderId,
  isProposalAnalysisPage = false,
  onSwitchView,
  onDownload,
  isDownloading = false,
  isSwitchingView = false,
  isProposalSectionView = false,
  showDownloadDropdown = false,
  downloadButtonRef,
  projectId,
  orgId,
  showSaveButton = false,
  onSave,
  saveStatus = "idle",
  onTeamMembersClick,
  dueDate = null,

  isProjectPage = false,
  projectData = {},
  projectHandlers = {},
  createManageDropdownRef,
  isCreateManageDropdownOpen,
  setIsCreateManageDropdownOpen,
  subscriptionChecked,
  isPaidYearlyPlan,

  isKnowledgeBasePage = false,
  kbState = {},
  kbHandlers = {},
  
  headerRightContent,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isKbTooltipVisible, setIsKbTooltipVisible] = useState(false);

  const kbDropdownRef = useRef(null);

  const internalCreateManageRef = useRef(null);
  const [localCreateManageOpen, setLocalCreateManageOpen] = useState(false);

  const activeDropdownRef = createManageDropdownRef || internalCreateManageRef;
  const isDropdownOpen = isCreateManageDropdownOpen !== undefined ? isCreateManageDropdownOpen : localCreateManageOpen;

  const toggleDropdown = () => {
    if (setIsCreateManageDropdownOpen) setIsCreateManageDropdownOpen(!isDropdownOpen);
    else setLocalCreateManageOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    if (setIsCreateManageDropdownOpen) setIsCreateManageDropdownOpen(false);
    else setLocalCreateManageOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kbDropdownRef.current && !kbDropdownRef.current.contains(event.target)) {
        setIsKbTooltipVisible(false);
      }
      if (activeDropdownRef.current && !activeDropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdownRef]);

  const [isGeneratingProposal, setIsGeneratingProposal] = React.useState(false);

  const isCreateProposalQuestionPage = location.pathname.includes(
    "/create-proposal-question/"
  );

  const pdfKeyOnRoute = React.useMemo(() => {
    if (!isCreateProposalQuestionPage) return null;
    const parts = location.pathname.split("/").filter(Boolean);
    const idx = parts.findIndex((p) => p === "create-proposal-question");
    return idx >= 0 && parts[idx + 1] ? parts[idx + 1] : null;
  }, [location.pathname, isCreateProposalQuestionPage]);

  const [downloading, setDownloading] = React.useState(false);
  const isCreatedProposalPage = location.pathname.includes("/created-proposal");

  const allSectionsCompleted =
    proposalProgress.total > 0 &&
    proposalProgress.completed === proposalProgress.total;

  const handleDownloadResponses = async () => {
    if (!pdfKeyOnRoute) return;
    try {
      setDownloading(true);
      const res = await api.get(
        `/api/v1/rfp/documents/${encodeURIComponent(
          pdfKeyOnRoute
        )}/export-questions-responses/`,
        { responseType: "blob" }
      );
      let filename = "responses.csv";
      const cd = res.headers?.["content-disposition"];
      if (cd) {
        const match = /filename\*?=(?:UTF-8''|")?([^;"']+)/i.exec(cd);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].replace(/"/g, ""));
        }
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerateProposal = () => {
    if (!pdfKeyOnRoute) return;
    navigate(`/created-proposal?pdf_key=${encodeURIComponent(pdfKeyOnRoute)}`);
  };

  const handleGenerateProposalClick = async () => {
    if (!folderId) {
      toast.error("Folder ID not found");
      return;
    }

    setIsGeneratingProposal(true);
    try {
      const response = await api.post(
        `api/v1/rfp/folders/${folderId}/generate-proposal/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 202
      ) {
        setTimeout(() => {
          setIsGeneratingProposal(false);
          toast.success("Proposal generated successfully!");
          const pathParts = window.location.pathname.split("/");
          const projectIdIndex = pathParts.indexOf("project") + 1;
          const projectId = pathParts[projectIdIndex];
          const orgIdIndex = pathParts.indexOf("org") + 1;
          const orgId = pathParts[orgIdIndex];

          if (projectId && orgId) {
            navigate(
              `/rfp-projects/project/${projectId}/org/${orgId}/proposal/analysis`,
              {
                state: {
                  proposalData: response.data,
                  projectName: name,
                  folderId: folderId,
                  orgId: orgId,
                },
              }
            );
          } else {
            navigate(`/rfp-projects/project/${projectId}/proposal/analysis`, {
              state: {
                proposalData: response.data,
                projectName: name,
                folderId: folderId,
              },
            });
          }
        }, 3000);
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error("Generate proposal error:", error);
      setIsGeneratingProposal(false);
      let errorMessage = "Failed to generate proposal";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleAskCopilotClick = () => {
    if (location.pathname.includes("/Invoices/Invoice_Details")) {
      navigate(
        `/Invoices/Invoice_chat?invoiceKey=${invoiceKey}&invoiceUrl=${encodeURIComponent(
          invoiceUrl
        )}&cid=${encodeURIComponent(invoicesId)}`
      );
    } else if (location.pathname.includes("/Spreadsheets/ExcelChat")) {
      navigate(
        `/Spreadsheets/ExcelChat?excelKey=${excelKey}&excelUrl=${encodeURIComponent(
          excelUrl
        )}`
      );
    } else {
      navigate(`/Contract/contractChat?ckey=${contractKey}&cid=${contractId}`);
    }
  };

  const handleGoToContractsClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    const currentPath = location.pathname.replace(/\/$/, "");

    if (
      currentPath.endsWith("/proposal") ||
      currentPath.includes("/proposal/analysis")
    ) {
      if (projectId && orgId) {
        navigate(`/rfp-projects/project/${projectId}/org/${orgId}`);
      } else if (projectId) {
        navigate(`/rfp-projects/project/${projectId}`);
      } else {
        navigate(-1);
      }
      return;
    }

    if (name === "Go to My Projects") {
      navigate(-1);
      return;
    }
    if (onGoToInvoices) {
      onGoToInvoices();
      return;
    }
    if (location.pathname.includes("/rfp-knowledgeBase/folder/")) {
      navigate("/rfp-knowledgeBase");
      return;
    }
    if (location.pathname.includes("/create-proposal-question")) {
      navigate("/Response_writer");
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const folderIdFromUrl = searchParams.get("folderId");

    if (location.pathname === "/Invoices") return;
    if (location.pathname === "/Spreadsheet") return;

    if (folderIdFromUrl) {
      navigate(`/uploadFile`);
    } else if (location.pathname.includes("/Contract/contract-details")) {
      navigate("/Contract");
    } else if (location.pathname.includes("/created-proposal")) {
      navigate("/Response_writer");
    } else if (location.pathname.includes("/Contract/contractChat")) {
      navigate("/Contract");
    } else if (location.pathname.includes("/Rfp/Rfp_chat")) {
      navigate("/rfp-analyze");
    } else if (location.pathname === "/Contract") {
      window.location.reload();
    } else if (location.pathname === "/uploadFile") {
      window.location.reload();
    } else if (
      location.pathname.includes("/chat") ||
      location.pathname.includes("/Contract/FolderChat")
    ) {
      navigate("/uploadFile");
    } else {
      navigate("/ai-copilot");
    }
  };

  const isContractChatPage = location.pathname.includes("contractChat");
  const isChatPage = location.pathname.includes("/chat");
  const isFolderChat = location.pathname.includes("/Contract/FolderChat");
  const isInvoiceChatPage = location.pathname.includes(
    "/Invoices/Invoice_chat"
  );
  const isRfpChatPage = location.pathname.includes("/Rfp/Rfp_chat");
  const isExcelChatPage = location.pathname.includes("/Spreadsheets/ExcelChat");
  const isPricingPage = location.pathname.includes("/payment");

  const handleSwitchToEditorWithRefresh = async () => {
    const pathParts = window.location.pathname.split("/");
    const projectIndex = pathParts.indexOf("project");
    const urlFolderId =
      projectIndex !== -1 ? pathParts[projectIndex + 1] : null;
    const orgIndex = pathParts.indexOf("org");
    const urlOrgId = orgIndex !== -1 ? pathParts[orgIndex + 1] : null;

    if (!urlFolderId) {
      toast.error("Could not find Project/Folder ID in URL");
      return;
    }

    setIsGeneratingProposal(true);

    try {
      const response = await api.post(
        `api/v1/rfp/folders/${urlFolderId}/generate-proposal/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Content updated successfully!");
        if (urlFolderId && urlOrgId) {
          navigate(
            `/rfp-projects/project/${urlFolderId}/org/${urlOrgId}/proposal/analysis`,
            {
              state: {
                proposalData: response.data,
                projectName: name,
                folderId: urlFolderId,
                orgId: urlOrgId,
              },
            }
          );
        } else {
          navigate(`/rfp-projects/project/${urlFolderId}/proposal/analysis`, {
            state: {
              proposalData: response.data,
              projectName: name,
              folderId: urlFolderId,
            },
          });
        }
      }
    } catch (error) {
      console.error("Switch view refresh error:", error);
      toast.error("Failed to refresh proposal content");
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  return (
    <div className="w-full">
      <header className="flex items-center w-full h-[65px] bg-[#F7F7F7] border-b border-[#0000001A] px-4">
        <button
          className="flex items-center cursor-pointer bg-transparent border-none flex-shrink-0 min-w-0"
          onClick={handleGoToContractsClick}
        >
          {icon && <img src={icon} alt="icon" className="h-5 w-5 mr-2" />}
          <span
            className="mr-4 text-[#52525B] font-outfit text-[18px] font-medium truncate"
            style={{ lineHeight: "normal" }}
          >
            {name}
          </span>
          {image && (
            <img
              src={image}
              alt="Header Logo"
              className="w-[144px] h-[26px] ml-4"
            />
          )}
        </button>

        <div className="ml-auto flex items-center gap-3 flex-shrink-0">

          {/* Right-most via order-last. Hidden on the notifications page itself. */}
          {location.pathname !== "/notifications" && (
            <div className="order-last flex items-center">
              <NotificationBell />
            </div>
          )}

          {headerRightContent && (
            <div className="flex items-center">
              {headerRightContent}
            </div>
          )}

          {isKnowledgeBasePage && (
            <>
              <ReviewerActionGate active={kbState.isReviewer} place="bottom">
                <button
                  onClick={kbHandlers.onCreateFolder}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-[#A04A97] bg-white hover:bg-pink-50 transition-colors"
                >
                  <img
                    src="/upload-doc-create.svg"
                    alt="Create Folder"
                    className="w-5 h-5"
                  />
                  <span className="text-[#A04A97] font-[Outfit] text-[14px] font-semibold leading-[24px] tracking-[0.14px]">
                    Create Folder
                  </span>
                </button>
              </ReviewerActionGate>

              <div className="relative flex gap-2" ref={kbDropdownRef}>
                <ReviewerActionGate active={kbState.isReviewer} place="bottom">
                <button
                  className={`flex items-center gap-2 px-5 py-2 bg-white border border-[#A04A97] transition-colors rounded-md text-[14px] ${
                    kbState.isProcessing
                      ? 'opacity-50 cursor-not-allowed bg-gray-100 border-gray-300 text-gray-400'
                      : 'hover:bg-pink-50 text-[#A04A97]'
                  }`}
                  onClick={() => {
                    if (!kbState.isProcessing) setIsKbTooltipVisible(!isKbTooltipVisible);
                  }}
                  disabled={kbState.isProcessing}
                >
                  <UploadFile />
                  <span className={`${kbState.isProcessing ? 'text-gray-400' : 'text-[#A04A97]'} font-outfit font-medium`}>
                    {kbState.isProcessing ? 'Processing...' : 'Upload Documents'}
                  </span>
                </button>
                </ReviewerActionGate>

                {isKbTooltipVisible && (
                  <div className="absolute right-0 top-full mt-2 z-[9999] w-[188px] flex flex-col justify-center items-start gap-2 p-[10px] bg-white border border-black/20 rounded-[12px] shadow-lg">
                    <div
                      className="flex items-center gap-2 w-full px-3 py-1.5 rounded-[9px] text-[#73607B] font-medium text-[15px] hover:bg-[#8B55DD]/20 hover:text-[#8B55DD] cursor-pointer transition-all"
                      onClick={() => {
                        setIsKbTooltipVisible(false);
                        kbHandlers.onAddDevice();
                      }}
                    >
                      <img src="/upload-icon.svg" alt="Upload" className="w-4 h-4" /> Add from device
                    </div>
                    {!kbState.isDriveConnected ? (
                      <div
                        className="flex items-center gap-2 w-full px-3 py-1.5 rounded-[9px] text-[#73607B] font-medium text-[15px] hover:bg-[#8B55DD]/20 hover:text-[#8B55DD] cursor-pointer transition-all"
                        onClick={() => {
                          setIsKbTooltipVisible(false);
                          kbHandlers.onConnectDrive();
                        }}
                      >
                        <img src="/connect-icon.svg" alt="Connect Drive" className="w-4 h-4" /> Connect Drive
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-[9px] text-[#73607B] font-medium text-[15px] hover:bg-[#8B55DD]/20 hover:text-[#8B55DD] cursor-pointer transition-all ${
                          !kbState.pickerLoaded ? "opacity-60 pointer-events-none" : ""
                        }`}
                        onClick={() => {
                          setIsKbTooltipVisible(false);
                          kbHandlers.onAddFromDrive();
                        }}
                      >
                        <img src="/picker-icon.svg" alt="Picker" className="w-4 h-4" /> Add From Drive
                      </div>
                    )}
                    {kbState.isSFDCConnected ? (
                      <button
                        className="flex items-center gap-2 w-full px-3 py-1.5 rounded-[9px] text-[#73607B] font-medium text-[15px] hover:bg-[#8B55DD]/20 hover:text-[#8B55DD] cursor-pointer transition-all"
                        onClick={() => {
                          setIsKbTooltipVisible(false);
                          kbHandlers.onAddFromSFDC();
                        }}
                      >
                        <img src="/salesforce.png" alt="Salesforce" className="w-4 h-4" /> Add from SFDC
                      </button>
                    ) : (
                      <button
                        className="flex items-center gap-2 w-full px-3 py-1.5 rounded-[9px] text-[#73607B] font-medium text-[15px] hover:bg-[#8B55DD]/20 hover:text-[#8B55DD] cursor-pointer transition-all"
                        onClick={() => {
                          setIsKbTooltipVisible(false);
                          kbHandlers.onConnectSFDC();
                        }}
                      >
                        <img src="/salesforce.png" alt="Connect Salesforce" className="w-4 h-4" /> Connect SFDC
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                className={`inline-flex items-center justify-center gap-2 px-2 py-2 rounded-md border transition-colors ${
                  kbState.isKnowledgeBaseEmpty
                    ? "border-[#A04A97]/30 bg-white/50 cursor-pointer hover:bg-white/70"
                    : "border-[#A04A97] bg-[#A04A97] text-white"
                }`}
                onClick={kbHandlers.onChat}
              >
                <ChatIconSvg
                  className="w-4 h-4"
                  style={{ color: kbState.isKnowledgeBaseEmpty ? "#A04A97" : "#BB42AE" }}
                />
                <span
                  className={`font-[Outfit] text-[14px] font-medium leading-[24px] tracking-[0.14px] ${
                    kbState.isKnowledgeBaseEmpty ? "text-[#A04A9766]" : "text-white"
                  }`}
                >
                  Chat with Knowledge Base
                </span>
              </button>
            </>
          )}

          {isProjectPage && !projectData.isLoading && (
            <>
              {/* --- FIXED: Create and Manage Dropdown --- */}
              <div className="relative inline-block text-left" ref={activeDropdownRef}>
                <button
                  type="button"
                  className="flex font-outfit items-center gap-2 px-3 py-2 bg-white text-[14px] border-[1px] border-[#DEDEDE] text-[#A04A97] rounded-md font-medium hover:bg-[#fae2f2] transition-colors"
                  onClick={toggleDropdown}
                >
                  <CreateManageIconSvg className="w-5 h-5" />
                  {(projectData.files?.user_permission || projectData.user_permission) === "reviewer"
                    ? "View Proposal"
                    : (projectData.files?.proposal_available || projectData.proposal_available)
                    ? "Manage Proposal"
                    : "Create Proposal"}
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      {(() => {
                        const userPermission = projectData.files?.user_permission || projectData.user_permission;
                        const hasProposal = projectData.files?.proposal_available || projectData.proposal_available;
                        const hasDocs = Array.isArray(projectData.files) 
                          ? projectData.files.length > 0 
                          : (projectData.files?.documents?.length > 0 || projectData.documents?.length > 0);
                        const isProcessing = projectData.isProcessing || false;

                        return userPermission === "reviewer" ? (
                          <button
                            type="button"
                            className="block w-full text-left px-4 py-2 text-sm text-[#A04A97] hover:text-[#A04A97] font-medium hover:bg-gray-100 font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => {
                              if (projectHandlers.onCreateProposal) projectHandlers.onCreateProposal(e);
                            }}
                            disabled={!hasProposal || projectData.isGeneratingProposal || projectData.isGenerating}
                            role="menuitem"
                          >
                            {projectData.isGeneratingProposal || projectData.isGenerating
                              ? "Loading..."
                              : "View Proposal"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="block w-full text-left px-4 py-2 text-sm text-[#A04A97] hover:text-[#A04A97] font-medium hover:bg-gray-100 font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => {
                              if (projectHandlers.onCreateProposal) projectHandlers.onCreateProposal(e);
                            }}
                            disabled={projectData.isGenerating || !hasDocs || (!hasProposal && isProcessing)}
                            role="menuitem"
                          >
                            {projectData.isGenerating && hasProposal
                              ? "Loading..."
                              : projectData.isGenerating
                              ? "Generating..."
                              : isProcessing && !hasProposal
                              ? "Processing files..."
                              : hasProposal
                              ? "Manage Proposal"
                              : "Create Proposal"}
                          </button>
                        );
                      })()}
                      
                      <button
                        type="button"
                        className="block w-full text-left px-4 py-2 text-sm text-[#A04A97] hover:text-[#A04A97] font-medium hover:bg-gray-100 font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          if (projectHandlers.onAnswerQuestionnaire) projectHandlers.onAnswerQuestionnaire(e);
                        }}
                        disabled={projectData.isAnsweringQuestionnaire}
                        role="menuitem"
                      >
                        {projectData.isAnsweringQuestionnaire ? "Loading..." : "Answer Questionnaire"}
                      </button>

                      <button
                        type="button"
                        className="block w-full text-left px-4 py-2 text-sm text-[#A04A97] hover:text-[#A04A97] font-medium hover:bg-gray-100 font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => {
                          if (projectHandlers.onFillForms) projectHandlers.onFillForms(e);
                        }}
                        disabled={projectData.isFillingForms}
                        role="menuitem"
                      >
                        {projectData.isFillingForms ? "Loading..." : "Fill Forms"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {projectData.files?.user_permission !== "reviewer" && (
                <div className="relative group upload-container-invoice">
                  <button
                    className="flex font-outfit items-center gap-2 text-[14px] px-2 py-[6px] bg-white border-[1px] border-[#DEDEDE] text-[#A04A97] rounded-md font-medium hover:bg-[#fae2f2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onMouseEnter={() => setIsTooltipVisible(true)}
                    onMouseLeave={() => setIsTooltipVisible(false)}
                    onClick={(e) => {
                      const hiddenInput = document.getElementById("project-hidden-file-input");
                      if (hiddenInput) {
                        hiddenInput.click();
                      } else if (projectHandlers.onUploadTrigger) {
                        projectHandlers.onUploadTrigger(e);
                      }
                    }}
                    disabled={
                      projectData.isUploadIngestBusy ||
                      (subscriptionChecked &&
                        projectData.files?.documents?.length >
                          (isPaidYearlyPlan ? 10 : 5))
                    }
                  >
                    <UploadIconSvg className="w-5 h-5" />
                    Upload File
                  </button>
                </div>
              )}

              <button
                className="flex font-outfit items-center text-[14px] gap-2 px-2 py-[6px] bg-[#A04A97] border border-[#A04A97] text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={projectHandlers.onChat}
                disabled={
                  Array.isArray(projectData.files)
                    ? projectData.files.length === 0
                    : !(
                        projectData.files?.documents?.length > 0 ||
                        projectData.documents?.length > 0
                      )
                }
              >
                <ChatIconSvg className="w-4 h-4" />
                Chat With Project
              </button>

              {(projectData.analysisReady || projectData.analysisLoading) && (
                <button
                  className="flex font-outfit items-center text-[14px] gap-2 px-3 py-[6px] bg-white border border-[#DEDEDE] text-[#A04A97] rounded-md font-medium hover:bg-[#fae2f2] transition-colors disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-white"
                  onClick={projectHandlers.onViewAnalysis}
                  disabled={projectData.analysisLoading}
                >
                  {projectData.analysisLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-[#A04A97] border-t-transparent rounded-full animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    "View Analysis"
                  )}
                </button>
              )}
            </>
          )}

          {isProposalSectionView && (
            <button
              onClick={handleSwitchToEditorWithRefresh}
              disabled={isGeneratingProposal}
              className={`flex h-9 px-3 py-2 justify-center items-center gap-1.5 rounded-md border border-[#A04A97] bg-white transition-colors ${
                isGeneratingProposal
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-purple-50"
              }`}
            >
              {isGeneratingProposal ? (
                <span className="font-outfit text-[#A04A97] text-[15px] font-medium">Switching...</span>
              ) : (
                <>
                  <SwitchToEditorViewIconSvg />
                  <span className="font-outfit text-[#A04A97] text-[15px] font-medium">Switch to Editor View</span>
                </>
              )}
            </button>
          )}

          {isProposalAnalysisPage && (
            <>
              {showSaveButton && (
                <button
                  onClick={onSave}
                  disabled={saveStatus === "saving"}
                  className="flex h-9 px-3 py-2 justify-center items-center gap-1.5 rounded-md border border-[#A04A97] bg-white hover:bg-purple-50 transition-colors disabled:opacity-60"
                >
                  <span className="font-outfit text-[#A04A97] text-[15px] font-medium">
                    {saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : "Save"}
                  </span>
                </button>
              )}

              {/* Switch to Section View button hidden permanently
              <button
                onClick={() => navigate(-1)}
                disabled={isSwitchingView}
                className="flex h-9 px-3 py-2 justify-center items-center gap-1.5 rounded-md border border-[#A04A97] bg-white hover:bg-purple-50 transition-colors disabled:opacity-60"
              >
                {isSwitchingView ? (
                  <span className="font-outfit text-[#A04A97] text-[15px] font-medium">Switching...</span>
                ) : (
                  <>
                    <SwitchToSectionViewIconSvg />
                    <span className="font-outfit text-[#A04A97] text-[15px] font-medium">Switch to Section View</span>
                  </>
                )}
              </button>
              */}

              <div className="relative" ref={downloadButtonRef}>
                <button
                  onClick={onDownload}
                  disabled={isDownloading}
                  className="flex h-9 px-3 py-2 justify-center items-center gap-1.5 rounded-md border border-[#A04A97] bg-[#A04A97] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  <DownloadIconSvg />
                  <span className="font-outfit !text-white text-[15px] font-medium">
                    {isDownloading ? "Downloading..." : "Download"}
                  </span>
                </button>
              </div>
            </>
          )}

          {isProposalPage && (
            <>
              {dueDate && (
                <span className="font-outfit text-[#52525B] text-[15px] font-medium whitespace-nowrap mr-1">
                  Due {formatDueDate(dueDate)}
                </span>
              )}

              <button
                onClick={onTeamMembersClick}
                className={`flex h-9 px-3 py-2 justify-center font-outfit text-white text-[15px] font-medium items-center gap-1.5 rounded-md border border-[#A04A97] bg-[#A04A97] ${
                  isGeneratingProposal ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 transition-opacity"
                }`}
              >
                <TeamIcon />
                <span>Team Members</span>
              </button>

              {/* Switch to Doc View button hidden permanently
              <button
                onClick={handleGenerateProposalClick}
                disabled={isGeneratingProposal}
                className={`flex h-9 px-3 py-2 justify-center font-outfit text-[#A04A97] text-[15px] font-medium items-center gap-1.5 rounded-md border border-[#A04A97] bg-white ${
                  isGeneratingProposal ? "opacity-70 cursor-not-allowed" : "hover:bg-purple-50 transition-colors"
                }`}
              >
                {isGeneratingProposal ? (
                  <span>Switching...</span>
                ) : (
                  <>
                    <GenerateProposalIconSvg />
                    <span>Switch to Doc View</span>
                  </>
                )}
              </button>
              */}
            </>
          )}

          {askCoPiolt && (
            <button className="ask-copilot-button flex justify-center" onClick={handleAskCopilotClick}>
              <img src="/ai-icon.svg" alt="AI Icon" className="h-4 w-4" />
              <span>Ask Copilot</span>
            </button>
          )}

          {(isCreateProposalQuestionPage || bell) && (
            <>
              {isCreateProposalQuestionPage && (
                <>
                  <button onClick={handleDownloadResponses} className={`download-response-btn ${downloading ? "disabled" : ""}`} disabled={downloading}>
                    {downloading ? "Downloading…" : "Download Response"}
                  </button>
                  <button onClick={handleGenerateProposal} className="download-response-btn">
                    Generate Proposal
                  </button>
                </>
              )}
              {bell && (
                <div className="flex justify-center items-center">
                  <button className="bg-transparent border-none">
                    <img src={bell} alt="Bell Icon" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </header>
    </div>
  );
};